import React, { useCallback, useMemo, useState } from "react";
import "../../pages/Dashboard.scss";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import {
  setProcurementFilters,
  resetProcurementFilters,
} from "../../store/filtersSlice";
import { Modal } from "@mui/material";
import { Agents } from "../../types/master";
import { getCategoryIcon } from "../../utils/agentCategoryIcons";
type WorkflowAgent = {
  id: number;
  name: string;
  description: string;
  category: string;
  procurementType?: "reviewer";
  customIcon?: string;
  subAgentsText?: string;
};
// Special agent configurations - for agents requiring custom behavior
const AGENT_SPECIAL_CONFIG: Record<number, Partial<WorkflowAgent>> = {
  20: {
    procurementType: "reviewer",
    subAgentsText:
      "Contract Reviewer: Legal Terms + FinanceTerms + Procurement Terms + Business Terms + Review Consolidator & Negotiation List Builder + Redraft",
  },
  46: { procurementType: "reviewer" },
  26: { procurementType: "reviewer" },
  18: {
    subAgentsText:
      "Candidate Fit Assessor + Talent Tracker: Location and Company Insights + First-Round Interview Screener + Talent Reconnect Emailer",
  },
  21: {
    subAgentsText:
      "Business & Commercial Checklist + Financial & Payment Checklist + Procurement & Legal Checklist",
  },
};
// Agents that should be highlighted - can be configured by agent ID
const HIGHLIGHTED_AGENT_IDS = new Set([11, 18, 20]);
// Agent IDs that require external endpoint access
const EXTERNAL_ENDPOINT_AGENTS = new Set([
  19, 31, 32, 33, 34, 27, 36, 49, 51, 50, 53, 56,
]);
// Categories to exclude from display
const EXCLUDED_CATEGORIES = new Set(["Terms"]);
type WorkflowsByFunctionProps = {
  onAgentClick: (agentId: number) => void;
};
export default function WorkflowsByFunction({
  onAgentClick,
}: WorkflowsByFunctionProps): JSX.Element {
  const allAgents = useSelector((state: RootState) => state.agents.data);
  const dispatch = useAppDispatch();
  // Dynamically extract categories and group agents by category
  const { categories, workflowsByCategory } = useMemo(() => {
    // Group agents by category
    const categoryMap = new Map<string, WorkflowAgent[]>();
    allAgents.forEach((agent) => {
      if (!agent.category || EXCLUDED_CATEGORIES.has(agent.category)) return;
      const workflow: WorkflowAgent = {
        id: agent.id,
        name: agent.agent_name,
        description: agent.short_description,
        category: agent.category,
        // Merge any special configuration for this agent
        ...AGENT_SPECIAL_CONFIG[agent.id],
      };
      if (!categoryMap.has(agent.category)) {
        categoryMap.set(agent.category, []);
      }
      categoryMap.get(agent.category)!.push(workflow);
    });

    // Sort agents within each category by name in ascending order
    categoryMap.forEach((agents) => {
      agents.sort((a, b) => {
        // Ensure agent names are strings and trim whitespace
        const nameA = (a.name || "").trim().toLowerCase();
        const nameB = (b.name || "").trim().toLowerCase();

        // Sort alphabetically in ascending order (A-Z)
        return nameA.localeCompare(nameB, undefined, {
          sensitivity: "base",
          numeric: true,
        });
      });
    });

    // Extract categories and sort them (you can customize the sort order)
    const sortedCategories = Array.from(categoryMap.keys()).sort((a, b) => {
      // Custom sort order - put certain categories first
      const order = [
        "CEO's Office",
        "Marketing",
        "Augmented HCC",
        "Procurement",
        "Legal",
        "Enterprise",
        "Commercial",
      ];
      const indexA = order.indexOf(a);
      const indexB = order.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
    return {
      categories: sortedCategories,
      workflowsByCategory: Object.fromEntries(categoryMap),
    };
  }, [allAgents]);
  const [activeCategory, setActiveCategory] = useState(
    categories[0] || "CEO's Office",
  );
  // Update active category if it becomes invalid (e.g., after data loads)
  React.useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);
  // Helper function to construct video blob URL
  const getVideoUrl = useCallback(
    (agentId: number): string | null => {
      const agent = allAgents.find((a) => a.id === agentId);
      if (!agent?.agent_intro_video_path) return null;
      return `${import.meta.env.VITE_TEMPLATE_BLOB_URL}/intro-videos/${agent.agent_intro_video_path}?${import.meta.env.VITE_TEMPLATE_G42_FILES}`;
    },
    [allAgents],
  );
  // Get workflows for the active category efficiently
  const currentWorkflows = useMemo(
    () => workflowsByCategory[activeCategory] || [],
    [activeCategory, workflowsByCategory],
  );
  // Helper function to check if agent has access
  const hasAgentAccess = useCallback(
    (agentId: number): boolean => {
      const agent = allAgents.find((a) => a.id === agentId);
      if (!agent) return false;
      // If agent requires external endpoint but doesn't have one, no access
      if (EXTERNAL_ENDPOINT_AGENTS.has(agentId) && !agent.endpoint) {
        return false;
      }
      return true;
    },
    [allAgents],
  );
  // Memoized icon rendering function
  const getIcon = useCallback((category: string, customIcon?: string) => {
    const iconSrc = getCategoryIcon(category, customIcon);
    return <img src={iconSrc} alt={`${category} icon`} />;
  }, []);

  const handleSelectAgent = useCallback(
    (id: number, procurementType?: "reviewer") => {
      if (procurementType) {
        dispatch(setProcurementFilters({ procurementType }));
      } else {
        dispatch(resetProcurementFilters());
      }
      onAgentClick(id);
    },
    [onAgentClick, dispatch],
  );
  const [openVideoAgentId, setOpenVideoAgentId] = useState<number | null>(null);
  const handleVideoClick = (agentId: number) => {
    setOpenVideoAgentId(agentId);
  };
  const closeVideoModal = () => {
    setOpenVideoAgentId(null);
  };
  return (
    <div className="card workflows">
      <div className="card-header">
        <h3 style={{ fontFamily: "G42 Sans Black Regular" }}>
          Agents by Function
        </h3>
      </div>
      <p className="subtle">
        Explore reimagined processes for your function powered by agents.
      </p>
      <div className="tabs">
        {categories.map((category) => (
          <button
            key={category}
            className={`tab ${activeCategory === category ? "active" : ""}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>
      <div className="workflow-grid">
        {currentWorkflows.map((workflow) => {
          const shouldHighlight = HIGHLIGHTED_AGENT_IDS.has(workflow.id);
          const hasVideo = !!getVideoUrl(workflow.id);
          const hasAccess = hasAgentAccess(workflow.id);
          return (
            <div
              key={workflow.id}
              className={`workflow-item ${
                shouldHighlight ? "workflow-item-highlight" : ""
              }`}
            >
              <div className="workflow-main">
                <div className="workflow-header">
                  {getIcon(workflow.category, workflow.customIcon)}
                  <div className="workflow-info">
                    <div className="wf-title">{workflow.name}</div>
                    {/* {workflow.subAgentsText && (
                      <div className="wf-sub">{workflow.subAgentsText}</div>
                    )} */}
                  </div>
                </div>
              </div>
              <div className="workflow-actions">
                <button
                  className="agent-video-icon workflow-video-trigger"
                  onClick={() => handleVideoClick(workflow.id)}
                  aria-label="Watch how it works"
                  disabled={!hasVideo}
                  style={{
                    opacity: hasVideo ? 1 : 0.5,
                    cursor: hasVideo ? "pointer" : "not-allowed",
                  }}
                >
                  <span className="workflow-video-icon-tooltip">
                    How it works
                  </span>
                  ▶
                </button>
                <button
                  className="deploy"
                  onClick={() =>
                    handleSelectAgent(workflow.id, workflow.procurementType)
                  }
                  disabled={!hasAccess}
                  style={{
                    opacity: hasAccess ? 1 : 0.5,
                    cursor: hasAccess ? "pointer" : "not-allowed",
                  }}
                >
                  <span>Launch</span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <Modal
        open={openVideoAgentId !== null}
        onClose={closeVideoModal}
        className="agent-video-modal"
        aria-labelledby="workflow-video-modal-title"
        aria-describedby="workflow-video-modal-description"
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
          },
        }}
      >
        <div className="agent-video-modal-content">
          <button
            className="agent-video-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              closeVideoModal();
            }}
            aria-label="Close video modal"
          >
            ✕
          </button>
          {openVideoAgentId !== null && (
            <video
              src={getVideoUrl(openVideoAgentId) || ""}
              controls
              autoPlay
              className="agent-video-modal-player"
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
